import type { Language } from '@eformulieren/engine'
import { type Field, type FieldId, type RowId, newId } from '@eformulieren/model'
import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import * as Y from 'yjs'
import { useSnapshot } from '../../document/snapshot.js'
import { useRowIdsOf } from '../../document/useSectionDocument.js'
import {
  type RowOrderUndo,
  addRowTo,
  isMergedText,
  moveRowBy,
  moveRowTo,
  orderedRows,
  removeRowFrom,
  rowById,
  rowOrderUndo,
} from '../../document/sectionDocument.js'
import { ArrowDownIcon } from '../../icons/ArrowDownIcon.js'
import { ArrowUpIcon } from '../../icons/ArrowUpIcon.js'
import { GripIcon } from '../../icons/GripIcon.js'
import { inLanguage } from '../../language/text.js'
import type { RowsProps } from './kinds.js'
import { RowDragArea, RowInsertLine, useRowHandle } from './rowDrag.js'

/**
 * De herhaalgroep: het invulbare containertype (R-40 tot R-43).
 *
 * **Een zelf samengestelde herhaalgroep begint leeg** (R-44 is vervallen): rijen
 * overnemen uit een externe bron is voorbehouden aan vaste componenten. Er staat dus
 * geen eerste rij klaar; de invuller voegt hem toe.
 *
 * **Per rij dezelfde veldset met dezelfde validatie per veld** (R-41). De cellen
 * worden getekend door `renderRowField` van de aanroeper, met de map van die ene rij
 * erbij: elke cel abonneert zich op zijn eigen sleutel, zodat typen in rij 3 rij 1
 * niet hertekent.
 *
 * **Het aantal is een regelsoort** (R-42). De control gebruikt de getallen alleen om
 * te weten of hij nog een rij mag aanbieden; de toets blijft de evaluator, en die
 * meldt op het moment van R-25. Een maximum of minimum bereikt betekent hier: de knop
 * weg of uit, met uitleg erbij, en niet een stille weigering.
 *
 * **Een rij verwijderen gaat op rij-id** en niet op plaats: wie op index verwijdert
 * haalt bij een gelijktijdige invoeging de verkeerde rij weg.
 *
 * **Rijen ordenen (besluit van 31 oktober 2026, ontwerp 6.8).** Elke rij heeft in haar kop
 * Omhoog en Omlaag, en Alt met een pijltje in de kop doet hetzelfde; met de muis of een vinger
 * sleept de invuller de greep (`rowDrag.tsx`). Alle drie roepen dezelfde verplaatsing aan
 * (`moveRowTo`): de rij blijft dezelfde Yjs-map met dezelfde rij-id, alleen haar volgordesleutel
 * verandert, dus een medeschrijver die tegelijk in die rij typt raakt niets kwijt (R-72), en
 * verwijzingen, correcties (R-201) en revisies blijven op dezelfde rij wijzen. Na een verplaatsing
 * blijft de focus op dezelfde knop in de verplaatste rij, en zegt een statusregel waar de rij nu staat.
 *
 * **Ongedaan maken komt van de samenwerklaag** (valkuil 5, `rowOrderUndo`): na een verplaatsing of
 * verwijdering staat "Ongedaan maken" onder de rijen, en Ctrl+Z (Cmd+Z) in de kop van een rij of in
 * die regel doet hetzelfde. Niet in een cel: daar is Ctrl+Z van de tekst zelf.
 *
 * **De kop van een rij noemt de rij** met haar nummer en de waarde van de eerste tekstkolom
 * ("Rij 1, WP1 Veldproef"), zodat een lange lijst te lezen is zonder elke rij open te lezen en de
 * statusregel zegt welke rij er verschoof.
 *
 * **Geen totaalregel.** R-45 zegt dat een totaal wordt getoond en niet getoetst, maar
 * de definitie kan nergens zeggen welke kolom een totaal krijgt: `herhaalgroep` heeft
 * in het model alleen `rijVelden`. Zelf iets optellen zou een gok zijn (een som van
 * jaartallen betekent niets), dus dit wacht op de instelling die erbij hoort. Zie
 * `docs/dekking.md`.
 */

const T = {
  row: { nl: 'Rij', en: 'Row' },
  up: { nl: 'omhoog', en: 'up' },
  down: { nl: 'omlaag', en: 'down' },
  undo: { nl: 'Ongedaan maken', en: 'Undo' },
  redo: { nl: 'Opnieuw', en: 'Redo' },
  undone: { nl: 'Ongedaan gemaakt.', en: 'Undone.' },
  redone: { nl: 'Opnieuw gedaan.', en: 'Redone.' },
  grip: { nl: 'Sleep om de rij te verplaatsen', en: 'Drag to move the row' },
} as const

/** De eerste tekstkolom: daar staat bij een rij het meest de naam van die rij. */
function summaryColumn(rowFields: readonly Field[]): FieldId | undefined {
  return rowFields.find((rowField) => rowField.control === 'text')?.id
}

function summaryOf(row: Y.Map<unknown>, column: FieldId | undefined): string {
  if (column === undefined) return ''
  const value = row.get(column)
  const text = value instanceof Y.Text ? value.toString() : typeof value === 'string' ? value : ''
  return text.trim()
}

/** Per rij-id de samenvatting, meelopend met wat er in de eerste tekstkolom wordt getypt. */
function useSummaries(list: Y.Array<Y.Map<unknown>>, column: FieldId | undefined): Readonly<Record<string, string>> {
  const subscribe = useCallback(
    (redraw: () => void) => {
      list.observeDeep(redraw)
      return () => list.unobserveDeep(redraw)
    },
    [list],
  )
  const read = useCallback(
    () => Object.fromEntries(orderedRows(list).map((row) => [String(row.get('rijId')), summaryOf(row, column)])),
    [list, column],
  )
  return useSnapshot(subscribe, read)
}

/** De ongedaan-maken-stapel van deze lijst, één per lijst zolang de groep in beeld is. */
function useRowUndo(list: Y.Array<Y.Map<unknown>>, enabled: boolean): { readonly undo: RowOrderUndo | undefined; readonly canUndo: boolean; readonly canRedo: boolean } {
  const [undo, setUndo] = useState<RowOrderUndo | undefined>(undefined)
  const [stacks, setStacks] = useState({ canUndo: false, canRedo: false })
  useEffect(() => {
    // Wie niet kan bewerken, heeft niets ongedaan te maken (ook in een revisie of de modus met historie).
    if (!enabled) return undefined
    const own = rowOrderUndo(list)
    setUndo(own)
    const update = (): void => setStacks({ canUndo: own.canUndo(), canRedo: own.canRedo() })
    const off = own.onChange(update)
    update()
    return () => {
      off()
      own.destroy()
      setUndo(undefined)
    }
  }, [list, enabled])
  return { undo, ...stacks }
}

const isUndoKey = (event: KeyboardEvent): boolean => (event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'z' && !event.shiftKey
const isRedoKey = (event: KeyboardEvent): boolean =>
  (event.ctrlKey || event.metaKey) && !event.altKey && ((event.key.toLowerCase() === 'z' && event.shiftKey) || event.key.toLowerCase() === 'y')

type Action = 'omhoog' | 'omlaag' | 'greep'

export function RepeatGroup({
  veld: field,
  taal: language,
  koppeling: link,
  lijst: list,
  renderRowField,
  bewerkbaar: editable,
  counts,
}: RowsProps): ReactNode {
  const rowIds = useRowIdsOf(list)
  const rowFields = 'rijVelden' in field ? field.rijVelden : []
  // Een rich-text-kolom krijgt zijn fragment bij het maken van de rij (zie
  // `addRowTo`); aanleggen tijdens het renderen zou het document veranderen
  // doordat iemand ernaar kijkt.
  const fragmentFields = rowFields.filter((rowField) => rowField.control === 'richtext').map((rowField) => rowField.id)
  // Een tekstkolom krijgt zijn `Y.Text` om dezelfde reden bij het maken van de rij (R-72).
  const textFields = rowFields.filter((rowField) => isMergedText(rowField.control)).map((rowField) => rowField.id)
  const maximumReached = counts?.maximum !== undefined && rowIds.length >= counts.maximum
  const minimumReached = counts?.minimum !== undefined && rowIds.length <= counts.minimum
  const summaries = useSummaries(list, summaryColumn(rowFields))
  const { undo, canUndo, canRedo } = useRowUndo(list, editable)
  const [status, setStatus] = useState('')
  const pendingFocus = useRef<{ readonly rowId: RowId; readonly action: Action } | 'ongedaan' | undefined>(undefined)
  /** Tot wanneer een focusopdracht geldt: een knop die er niet komt, mag de focus later niet alsnog pakken. */
  const pendingUntil = useRef(0)
  const groupRef = useRef<HTMLDivElement>(null)

  const word = inLanguage(T.row, language)
  const numberOf = (rowId: RowId): number => rowIds.indexOf(rowId) + 1
  /** "Rij 2, WP1 Veldproef", of "Rij 2" zolang de eerste tekstkolom leeg is. */
  const nameOf = (rowId: RowId, place = numberOf(rowId)): string => {
    const summary = summaries[rowId] ?? ''
    return summary === '' ? `${word} ${String(place)}` : `${word} ${String(place)}, ${summary}`
  }

  const moved = (rowId: RowId): void => {
    const order = orderedRows(list).map((row) => row.get('rijId'))
    const place = order.indexOf(rowId) + 1
    const summary = summaries[rowId] ?? ''
    const what = summary === '' ? (language === 'nl' ? 'De rij' : 'The row') : summary
    setStatus(
      language === 'nl'
        ? `${what} staat nu op plek ${String(place)} van ${String(order.length)}.`
        : `${what} is now in position ${String(place)} of ${String(order.length)}.`,
    )
  }

  const move = (rowId: RowId, delta: -1 | 1, action: Action): void => {
    const done = undo === undefined ? moveRowBy(list, rowId, delta) : undo.moveBy(rowId, delta)
    if (!done) return
    pendingFocus.current = { rowId, action }
    moved(rowId)
  }

  const moveTo = (rowId: RowId, before: RowId | undefined): boolean => {
    const done = undo === undefined ? moveRowTo(list, rowId, before) : undo.move(rowId, before)
    if (done) moved(rowId)
    return done
  }

  const remove = (rowId: RowId): void => {
    const name = nameOf(rowId)
    if (undo === undefined) removeRowFrom(list, rowId)
    else undo.remove(rowId)
    setStatus(language === 'nl' ? `${name} is verwijderd.` : `${name} has been removed.`)
    // De knop die werd ingedrukt is weg; de focus gaat naar Ongedaan maken, waar de melding staat.
    pendingFocus.current = 'ongedaan'
  }

  /**
   * `focusAfter`: waar de focus daarna hoort. Vanaf de knop is dat de regel met de knoppen, want
   * Ongedaan maken kan daarna weg zijn en dan staat Opnieuw op zijn plek; vanuit de kop van een rij
   * blijft hij op de knop waar hij stond.
   */
  type FocusAfter = typeof pendingFocus.current
  const undoOnce = (focusAfter: FocusAfter = 'ongedaan'): void => {
    if (undo === undefined || !undo.canUndo()) return
    undo.undo()
    setStatus(inLanguage(T.undone, language))
    pendingFocus.current = focusAfter
  }
  const redoOnce = (focusAfter: FocusAfter = 'ongedaan'): void => {
    if (undo === undefined || !undo.canRedo()) return
    undo.redo()
    setStatus(inLanguage(T.redone, language))
    pendingFocus.current = focusAfter
  }

  /**
   * Na een verplaatsing de focus terug op dezelfde knop in dezelfde rij. React verhuist het element
   * in de DOM, en een element dat verhuist kan zijn focus kwijtraken; de nieuwe volgorde komt pas na
   * de microtask van `useSnapshot`, dus dit wacht op de render waarin de rij op haar nieuwe plek staat.
   */
  useEffect(() => {
    const wanted = pendingFocus.current
    if (wanted === undefined) return
    if (pendingUntil.current === 0) pendingUntil.current = Date.now() + 1000
    if (Date.now() > pendingUntil.current) {
      pendingFocus.current = undefined
      pendingUntil.current = 0
      return
    }
    const group = groupRef.current
    if (group === null) return
    const target =
      wanted === 'ongedaan'
        ? group.querySelector<HTMLElement>('[data-rij-actie="ongedaan"], [data-rij-actie="opnieuw"]')
        : group.querySelector<HTMLElement>(`[data-rij="${wanted.rowId}"] [data-rij-actie="${wanted.action}"]`)
    if (target === null) return
    pendingFocus.current = undefined
    pendingUntil.current = 0
    if (document.activeElement !== target) target.focus()
  })

  /** Alt met een pijltje verplaatst; Ctrl+Z en Ctrl+Shift+Z (of Ctrl+Y) maken ongedaan en opnieuw. */
  const onHeaderKey = (rowId: RowId) => (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.altKey && !event.ctrlKey && !event.metaKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.preventDefault()
      const delta = event.key === 'ArrowUp' ? -1 : 1
      const focused = (event.target as HTMLElement).closest<HTMLElement>('[data-rij-actie]')?.dataset.rijActie
      move(rowId, delta, focused === 'omlaag' || focused === 'omhoog' ? focused : delta === -1 ? 'omhoog' : 'omlaag')
      return
    }
    const focused = (event.target as HTMLElement).closest<HTMLElement>('[data-rij-actie]')?.dataset.rijActie
    onUndoKey(event, focused === 'omhoog' || focused === 'omlaag' ? { rowId, action: focused } : undefined)
  }
  const onUndoKey = (event: KeyboardEvent<HTMLElement>, focusAfter: FocusAfter = 'ongedaan'): void => {
    if (isUndoKey(event) && canUndo) {
      event.preventDefault()
      undoOnce(focusAfter)
    } else if (isRedoKey(event) && canRedo) {
      event.preventDefault()
      redoOnce(focusAfter)
    }
  }

  const rows = (
    <ol className="ef-herhaalgroep__rijen">
      {rowIds.map((rowId, place) => {
        const row = rowById(list, rowId)
        if (row === undefined) return null
        return (
          <Row
            key={rowId}
            rowId={rowId}
            order={rowIds}
            editable={editable}
            number={place + 1}
            language={language}
            title={`${word} ${String(place + 1)}`}
            summary={summaries[rowId] ?? ''}
            first={place === 0}
            last={place === rowIds.length - 1}
            canRemove={!minimumReached}
            onUp={() => move(rowId, -1, 'omhoog')}
            onDown={() => move(rowId, 1, 'omlaag')}
            onRemove={() => remove(rowId)}
            onHeaderKey={onHeaderKey(rowId)}
            nameForLabel={nameOf(rowId, place + 1)}
          >
            {rowFields.map((rowField) => (
              <div className="ef-rij__cel" key={rowField.id}>
                {renderRowField(rowField, row, rowId)}
              </div>
            ))}
          </Row>
        )
      })}
    </ol>
  )

  return (
    <div className="ef-herhaalgroep" id={link.id} aria-describedby={link['aria-describedby']} ref={groupRef}>
      {rowIds.length === 0 ? (
        <p className="ef-herhaalgroep__leeg">
          {language === 'nl' ? 'Nog geen rijen.' : 'No rows yet.'}
        </p>
      ) : editable ? (
        <RowDragArea rowIds={rowIds} nameOf={(rowId) => nameOf(rowId)} onMove={moveTo}>
          {rows}
        </RowDragArea>
      ) : (
        rows
      )}

      {editable ? (
        <div className="ef-herhaalgroep__voet" onKeyDown={(event) => onUndoKey(event)}>
          <button
            type="button"
            className="ef-herhaalgroep__erbij"
            disabled={maximumReached}
            onClick={() => addRowTo(list, newId('rij'), fragmentFields, textFields)}
          >
            <span aria-hidden="true">+</span>
            {language === 'nl' ? 'Rij toevoegen' : 'Add row'}
          </button>
          {maximumReached ? (
            <span className="ef-herhaalgroep__grens">
              {language === 'nl'
                ? `Maximaal ${String(counts?.maximum)} rijen.`
                : `At most ${String(counts?.maximum)} rows.`}
            </span>
          ) : null}
          {minimumReached && rowIds.length > 0 ? (
            // Verwijderen staat dan niet bij de rij; hier staat waarom (audit van het formulier).
            <span className="ef-herhaalgroep__grens">{minimumText(counts?.minimum ?? 0, language)}</span>
          ) : null}
          <div className="ef-herhaalgroep__melding">
            {/* Altijd aanwezig, zodat een schermlezer de eerste melding ook voorleest. */}
            <span role="status" className="ef-herhaalgroep__status">
              {status}
            </span>
            {canUndo ? (
              <button type="button" className="ef-herhaalgroep__ongedaan" data-rij-actie="ongedaan" onClick={() => undoOnce()}>
                {inLanguage(T.undo, language)}
              </button>
            ) : null}
            {canRedo ? (
              <button type="button" className="ef-herhaalgroep__ongedaan" data-rij-actie="opnieuw" onClick={() => redoOnce()}>
                {inLanguage(T.redo, language)}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <span className="ef-sr-only">
        {field.label === undefined ? '' : inLanguage(field.label, language)}
      </span>
    </div>
  )
}

function minimumText(minimum: number, language: Language): string {
  if (language === 'nl') return minimum === 1 ? 'Minimaal 1 rij.' : `Minimaal ${String(minimum)} rijen.`
  return minimum === 1 ? 'At least 1 row.' : `At least ${String(minimum)} rows.`
}

interface RowProps {
  readonly rowId: RowId
  readonly order: readonly RowId[]
  readonly editable: boolean
  readonly number: number
  readonly language: Language
  readonly title: string
  readonly summary: string
  readonly first: boolean
  readonly last: boolean
  readonly canRemove: boolean
  readonly onUp: () => void
  readonly onDown: () => void
  readonly onRemove: () => void
  readonly onHeaderKey: (event: KeyboardEvent<HTMLDivElement>) => void
  readonly nameForLabel: string
  readonly children: ReactNode
}

function Row(props: RowProps): ReactNode {
  // Een bewerkbare rij is altijd sleepbaar, ook als ze alleen staat: wisselen tussen de twee bij de
  // tweede rij zou de cellen opnieuw opbouwen, met een rich-text-editor erin.
  return props.editable ? <DraggableRow {...props} /> : <RowBody {...props} />
}

function DraggableRow(props: RowProps): ReactNode {
  const handle = useRowHandle(props.rowId, props.order)
  return (
    <RowBody
      {...props}
      setRow={handle.setRow}
      dragging={handle.dragging}
      line={handle.line}
      grip={
        props.order.length < 2 ? undefined : <span className="ef-rij__greep" ref={handle.setGrip} {...handle.grip} aria-hidden="true" title={inLanguage(T.grip, props.language)} data-proef="rij-greep">
          <GripIcon />
        </span>
      }
    />
  )
}

function RowBody({
  rowId,
  order,
  editable,
  language,
  number,
  title,
  summary,
  first,
  last,
  canRemove,
  onUp,
  onDown,
  onRemove,
  onHeaderKey,
  nameForLabel,
  children,
  setRow,
  dragging = false,
  line,
  grip,
}: RowProps & {
  readonly setRow?: (element: HTMLElement | null) => void
  readonly dragging?: boolean
  readonly line?: 'voor' | 'na' | undefined
  readonly grip?: ReactNode
}): ReactNode {
  return (
    <li className="ef-rij" data-rij={rowId} data-sleept={dragging ? 'ja' : undefined} ref={setRow}>
      {line === undefined ? null : <RowInsertLine kant={line} />}
      <div className="ef-rij__kop" onKeyDown={editable ? onHeaderKey : undefined}>
        {grip}
        <span className="ef-rij__naam">
          <span className="ef-rij__nummer">{title}</span>
          {summary === '' ? null : <span className="ef-rij__samenvatting">{summary}</span>}
        </span>
        {editable ? (
          <span className="ef-rij__acties">
            {/* Eén rij heeft geen plek om heen te gaan; dan geen twee knoppen die niets doen. */}
            {order.length < 2 ? null : <>
            <MoveButton
              action="omhoog"
              label={`${nameForLabel} ${inLanguage(T.up, language)}`}
              disabled={first}
              onClick={onUp}
            />
            <MoveButton
              action="omlaag"
              label={`${nameForLabel} ${inLanguage(T.down, language)}`}
              disabled={last}
              onClick={onDown}
            />
            </>}
            {canRemove ? (
              <button
                type="button"
                className="ef-rij__weg"
                aria-label={language === 'nl' ? `Verwijder rij ${String(number)}` : `Remove row ${String(number)}`}
                onClick={onRemove}
              >
                {language === 'nl' ? 'Verwijderen' : 'Remove'}
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="ef-rij__cellen">{children}</div>
    </li>
  )
}

/**
 * Omhoog of Omlaag. Aan de rand `aria-disabled` en niet `disabled`: een uitgeschakelde knop verliest
 * de focus, en wie met Alt+pijl een rij naar boven brengt staat daarna precies op die knop.
 */
function MoveButton({ action, label, disabled, onClick }: { readonly action: 'omhoog' | 'omlaag'; readonly label: string; readonly disabled: boolean; readonly onClick: () => void }): ReactNode {
  return (
    <button
      type="button"
      className="ef-rij__verplaats"
      data-rij-actie={action}
      aria-label={label}
      aria-disabled={disabled ? 'true' : undefined}
      onClick={() => {
        if (!disabled) onClick()
      }}
    >
      {action === 'omhoog' ? <ArrowUpIcon aria-hidden="true" /> : <ArrowDownIcon aria-hidden="true" />}
    </button>
  )
}
